import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { Building2, Phone, MapPin, CheckCircle, AlertCircle, Ticket, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { completeProfile } from '../lib/api/auth';
import { COUNTRIES } from '../constants/countries';
import '../styles/auth.css';

const COUNTRY_CODES = [
  { value: '+91', label: '+91 India' },{ value: '+1', label: '+1 USA' },{ value: '+44', label: '+44 UK' },
  { value: '+61', label: '+61 Australia' },{ value: '+86', label: '+86 China' },{ value: '+81', label: '+81 Japan' },
  { value: '+82', label: '+82 South Korea' },{ value: '+65', label: '+65 Singapore' },{ value: '+60', label: '+60 Malaysia' },
  { value: '+63', label: '+63 Philippines' },{ value: '+62', label: '+62 Indonesia' },{ value: '+66', label: '+66 Thailand' },
  { value: '+84', label: '+84 Vietnam' },{ value: '+971', label: '+971 UAE' },{ value: '+966', label: '+966 Saudi Arabia' },
  { value: '+974', label: '+974 Qatar' },{ value: '+973', label: '+973 Bahrain' },{ value: '+968', label: '+968 Oman' },
  { value: '+965', label: '+965 Kuwait' },{ value: '+962', label: '+962 Jordan' },{ value: '+961', label: '+961 Lebanon' },
  { value: '+972', label: '+972 Israel' },{ value: '+90', label: '+90 Turkey' },{ value: '+7', label: '+7 Russia' },
  { value: '+49', label: '+49 Germany' },{ value: '+33', label: '+33 France' },{ value: '+34', label: '+34 Spain' },
  { value: '+39', label: '+39 Italy' },{ value: '+31', label: '+31 Netherlands' },{ value: '+32', label: '+32 Belgium' },
  { value: '+41', label: '+41 Switzerland' },{ value: '+43', label: '+43 Austria' },{ value: '+46', label: '+46 Sweden' },
  { value: '+47', label: '+47 Norway' },{ value: '+45', label: '+45 Denmark' },{ value: '+48', label: '+48 Poland' },
  { value: '+351', label: '+351 Portugal' },{ value: '+353', label: '+353 Ireland' },{ value: '+358', label: '+358 Finland' },
  { value: '+30', label: '+30 Greece' },{ value: '+36', label: '+36 Hungary' },{ value: '+40', label: '+40 Romania' },
  { value: '+380', label: '+380 Ukraine' },{ value: '+420', label: '+420 Czech Republic' },{ value: '+27', label: '+27 South Africa' },
  { value: '+234', label: '+234 Nigeria' },{ value: '+254', label: '+254 Kenya' },{ value: '+233', label: '+233 Ghana' },
  { value: '+256', label: '+256 Uganda' },{ value: '+255', label: '+255 Tanzania' },{ value: '+251', label: '+251 Ethiopia' },
  { value: '+20', label: '+20 Egypt' },{ value: '+212', label: '+212 Morocco' },{ value: '+213', label: '+213 Algeria' },
  { value: '+55', label: '+55 Brazil' },{ value: '+52', label: '+52 Mexico' },{ value: '+57', label: '+57 Colombia' },
  { value: '+56', label: '+56 Chile' },{ value: '+54', label: '+54 Argentina' },{ value: '+64', label: '+64 New Zealand' },
  { value: '+92', label: '+92 Pakistan' },{ value: '+94', label: '+94 Sri Lanka' },{ value: '+880', label: '+880 Bangladesh' },
  { value: '+977', label: '+977 Nepal' },{ value: '+852', label: '+852 Hong Kong' },{ value: '+886', label: '+886 Taiwan' },
  { value: '+960', label: '+960 Maldives' },{ value: '+230', label: '+230 Mauritius' },
];

export default function CompleteProfile() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { login } = useAuth(); // We might need to manually update auth state

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        organization: '',
        country: null,
        countryCode: { value: '+91', label: '+91 India' },
        phoneNumber: '',
        referralCode: ''
    });
    const [referralInfo, setReferralInfo] = useState(null);
    const [referralLoading, setReferralLoading] = useState(false);
    const [referralError, setReferralError] = useState('');
    const [hasReferralCode, setHasReferralCode] = useState(null);

    useEffect(() => {
        // Auth is enforced by ProtectedRoute (checks the cookie-authenticated
        // /me result before ever rendering this page) — issue #196 removed
        // the pre-existing ?token= handoff this used to also handle here,
        // which read a token this page had no other reason to touch.
        const refCode = searchParams.get('ref');
        if (refCode) {
            setFormData(prev => ({ ...prev, referralCode: refCode.toUpperCase() }));
            setHasReferralCode('yes');
            (async () => {
                try {
                    const { lookupFellowshipCode } = await import('../lib/api/fellowship');
                    const data = await lookupFellowshipCode(refCode);
                    setReferralInfo(data);
                } catch { setReferralError('Invalid referral code'); }
            })();
        }
    }, [searchParams, navigate]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSelectChange = (name, value) => {
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.organization || !formData.country || !formData.phoneNumber) {
            setError('Please fill in all required fields');
            return;
        }
        if (hasReferralCode === null) {
            setError('Please select whether you have a referral code');
            return;
        }

        setLoading(true);

        try {
            const profileData = {
                organization: formData.organization,
                country: formData.country.value,
                phone_number: `${formData.countryCode?.value || '+91'}${formData.phoneNumber}`,
                referral_code: formData.referralCode?.trim() || undefined
            };

            await completeProfile(profileData);

            // Force reload user context or redirect
            // Since completeProfile updates cached user, we can just navigate.
            // But we might want to refresh the AuthContext.
            window.location.href = '/generator'; // Hard reload to ensure context picks up new data

        } catch (err) {
            console.error('Profile completion error:', err);
            setError(err.message || 'Failed to update profile. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-container">
            <div className="auth-background"><div className="gradient-1"></div><div className="gradient-2"></div></div>
            <div className="auth-wrapper">
                <div className="auth-card">
                    <div className="auth-header">
                        <div className="auth-logo">
                            <picture>
                                <source srcSet="/TechGenieMascot.webp" type="image/webp" />
                                <img src="/TechGenieMascot.png" alt="TeachGenie™.ai" className="auth-logo-image" style={{ width: '50px', height: '50px', borderRadius: '50%' }} />
                            </picture>
                            <h1 className="animated-title">TeachGenie™.ai</h1>
                        </div>
                        <p className="auth-subtitle">Complete Your Profile</p>
                        <p className="text-sm text-slate-500 mt-2">
                            Please provide a few more details to finish setting up your account.
                        </p>
                    </div>

                    <div className="auth-form-container">
                        {error && (
                            <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm flex items-center gap-2 border border-red-200">
                                <AlertCircle size={16} /> {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="auth-form space-y-4">

                            <div className="form-group">
                                <label><Building2 size={20} /> Organization / Institution</label>
                                <input
                                    type="text"
                                    name="organization"
                                    value={formData.organization}
                                    onChange={handleChange}
                                    placeholder="e.g. University of Design"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label><MapPin size={20} /> Country</label>
                                <Select
                                    options={COUNTRIES}
                                    value={formData.country}
                                    onChange={(option) => handleSelectChange('country', option)}
                                    placeholder="Select Country"
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label><Phone size={20} /> Phone Number</label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <CreatableSelect
                                        options={COUNTRY_CODES}
                                        value={formData.countryCode}
                                        onChange={(option) => setFormData(prev => ({ ...prev, countryCode: option }))}
                                        placeholder="+91"
                                        formatCreateLabel={(input) => `Use: ${input}`}
                                        isSearchable={true}
                                        styles={{
                                            container: (base) => ({ ...base, width: '160px', flexShrink: 0 }),
                                            control: (base) => ({ ...base, minHeight: '44px', borderRadius: '8px', borderColor: '#e5e7eb' }),
                                            menu: (base) => ({ ...base, zIndex: 20 }),
                                            singleValue: (base) => ({ ...base, fontSize: '14px' }),
                                            option: (base) => ({ ...base, fontSize: '14px' }),
                                        }}
                                    />
                                    <input
                                        type="tel"
                                        name="phoneNumber"
                                        value={formData.phoneNumber}
                                        onChange={handleChange}
                                        placeholder="1234567890"
                                        required
                                        style={{ flex: 1 }}
                                    />
                                </div>
                            </div>

                            <div style={{ marginTop: '4px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '10px' }}>
                                    <Ticket size={18} style={{ color: '#6b7280' }} />
                                    Do you have a referral code?
                                </label>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button
                                        type="button"
                                        onClick={() => { setHasReferralCode('yes'); setError(''); }}
                                        style={{
                                            flex: 1, padding: '10px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '600',
                                            cursor: 'pointer', transition: 'all 0.2s ease',
                                            border: hasReferralCode === 'yes' ? '2px solid #1e3a8a' : '2px solid #d1d5db',
                                            background: hasReferralCode === 'yes' ? '#1e3a8a' : '#fff',
                                            color: hasReferralCode === 'yes' ? '#fff' : '#374151',
                                        }}
                                    >
                                        Yes
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setHasReferralCode('no');
                                            setFormData(prev => ({ ...prev, referralCode: '' }));
                                            setReferralInfo(null);
                                            setReferralError('');
                                            setError('');
                                        }}
                                        style={{
                                            flex: 1, padding: '10px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '600',
                                            cursor: 'pointer', transition: 'all 0.2s ease',
                                            border: hasReferralCode === 'no' ? '2px solid #1e3a8a' : '2px solid #d1d5db',
                                            background: hasReferralCode === 'no' ? '#1e3a8a' : '#fff',
                                            color: hasReferralCode === 'no' ? '#fff' : '#374151',
                                        }}
                                    >
                                        No
                                    </button>
                                </div>

                                {hasReferralCode === 'yes' && (
                                    <div className="form-group" style={{ marginTop: '12px' }}>
                                        <input
                                            type="text"
                                            name="referralCode"
                                            value={formData.referralCode}
                                            onChange={(e) => {
                                                const val = e.target.value.toUpperCase();
                                                setFormData(prev => ({ ...prev, referralCode: val }));
                                                setReferralInfo(null);
                                                setReferralError('');
                                            }}
                                            onBlur={async () => {
                                                const code = formData.referralCode.trim();
                                                if (code.length < 3) return;
                                                setReferralLoading(true);
                                                setReferralError('');
                                                try {
                                                    const { lookupFellowshipCode } = await import('../lib/api/fellowship');
                                                    const data = await lookupFellowshipCode(code);
                                                    setReferralInfo(data);
                                                } catch {
                                                    setReferralError('Invalid referral code');
                                                    setReferralInfo(null);
                                                } finally {
                                                    setReferralLoading(false);
                                                }
                                            }}
                                            placeholder="e.g. F-MXT23"
                                            style={{ textTransform: 'uppercase' }}
                                            autoFocus
                                        />
                                        {referralLoading && <p style={{ color: '#6b7280', fontSize: '13px', marginTop: '4px' }}>Checking code...</p>}
                                        {referralInfo && (
                                            <p style={{ color: '#059669', fontSize: '13px', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <CheckCircle2 size={14} /> Referred by: <strong>{referralInfo.referrer_name}</strong> ({referralInfo.referrer_level})
                                            </p>
                                        )}
                                        {referralError && <p style={{ color: '#dc2626', fontSize: '13px', marginTop: '4px' }}>{referralError}</p>}
                                    </div>
                                )}
                            </div>

                            <button
                                type="submit"
                                className="btn btn-accent btn-large w-full mt-6"
                                disabled={loading}
                            >
                                {loading ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                                        Saving...
                                    </span>
                                ) : (
                                    <span className="flex items-center justify-center gap-2">
                                        Complete Setup <CheckCircle size={18} />
                                    </span>
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}
